import { AppOptions, getCertificationDisplayName, getSpecialtyCertificationDisplayName, getGasCertificationDisplayName, getCertificationLevel } from './optionsManager';
import { DiveCertificationRequirement, DiveGasCertificationRequirement, DiveSite } from '../types/DiveSite';

type DiverProfile = AppOptions['diver'];

// Which specialty cert flags satisfy each DiveCertificationRequirement
const specialtyRequirementMatrix: Record<
  Exclude<DiveCertificationRequirement, 'AOW'>,
  Array<keyof AppOptions['diver']['specialtyCertifications']>
> = {
  Wreck: ['wreck', 'advancedWreck'],
  AdvancedWreck: ['advancedWreck'],
  Cavern: ['cavern', 'introCave', 'fullCave'],
  IntroCave: ['introCave', 'fullCave'],
  FullCave: ['fullCave'],
  Tec40: ['tec40', 'tec45', 'tec50', 'tecTrimix'],
  Tec45: ['tec45', 'tec50', 'tecTrimix'],
  Tec50: ['tec50', 'tecTrimix'],
  TecTrimix: ['tecTrimix'],
};

const gasRequirementMatrix: Record<DiveGasCertificationRequirement, DiveGasCertificationRequirement[]> = {
  nitrox: ['nitrox', 'advancedNitrox', 'trimix', 'advancedTrimix'],
  advancedNitrox: ['advancedNitrox', 'trimix', 'advancedTrimix'],
  trimix: ['trimix', 'advancedTrimix'],
  advancedTrimix: ['advancedTrimix'],
};

export interface DiveSiteRequirementStatus {
  requiredCertification?: DiveCertificationRequirement;
  requiredGasCertifications: DiveGasCertificationRequirement[];
  meetsCertificationRequirement: boolean;
  missingGasCertifications: DiveGasCertificationRequirement[];
  meetsRequirements: boolean;
  note?: string;
  chips: string[];
  warningMessage?: string;
}

const getEffectiveRequiredCertification = (site: DiveSite): DiveCertificationRequirement | undefined => {
  if (site.requiredCertification) {
    return site.requiredCertification;
  }

  if (site.difficulty === 'Advanced') {
    return 'AOW';
  }

  return undefined;
};

const getEffectiveRequirementNote = (site: DiveSite, requiredCertification?: DiveCertificationRequirement): string | undefined => {
  if (site.requirementNote) {
    return site.requirementNote;
  }

  if (requiredCertification === 'AOW' && site.difficulty === 'Advanced') {
    return 'This site is marked advanced in the catalog and is best approached with advanced-level training.';
  }

  return undefined;
};

const meetsCertificationRequirement = (
  diverProfile: DiverProfile,
  requiredCertification?: DiveCertificationRequirement
): boolean => {
  if (!requiredCertification) {
    return true;
  }

  // AOW requirement is met by the progression cert level
  if (requiredCertification === 'AOW') {
    return getCertificationLevel(diverProfile.certification) >= getCertificationLevel('AOW');
  }

  // All other requirements are met by specialtyCertifications flags
  const acceptedFlags = specialtyRequirementMatrix[requiredCertification];
  return acceptedFlags.some((flag) => diverProfile.specialtyCertifications[flag]);
};

const CERT_REQ_TO_SPECIALTY_KEY: Record<
  Exclude<DiveCertificationRequirement, 'AOW'>,
  keyof AppOptions['diver']['specialtyCertifications']
> = {
  Wreck: 'wreck',
  AdvancedWreck: 'advancedWreck',
  Cavern: 'cavern',
  IntroCave: 'introCave',
  FullCave: 'fullCave',
  Tec40: 'tec40',
  Tec45: 'tec45',
  Tec50: 'tec50',
  TecTrimix: 'tecTrimix',
};

const getCertReqDisplayName = (req: DiveCertificationRequirement): string => {
  if (req === 'AOW') {
    return getCertificationDisplayName('AOW');
  }
  return getSpecialtyCertificationDisplayName(CERT_REQ_TO_SPECIALTY_KEY[req]);
};

const meetsGasRequirement = (
  gasCertifications: DiverProfile['gasCertifications'],
  requirement: DiveGasCertificationRequirement
): boolean => {
  return gasRequirementMatrix[requirement].some((certification) => gasCertifications[certification]);
};

export const getDiveSiteRequirementStatus = (
  site: DiveSite,
  diverProfile: DiverProfile
): DiveSiteRequirementStatus => {
  const requiredCertification = getEffectiveRequiredCertification(site);
  const requiredGasCertifications = site.requiredGasCertifications ?? [];
  const meetsCertification = meetsCertificationRequirement(diverProfile, requiredCertification);
  const missingGasCertifications = requiredGasCertifications.filter(
    (requirement) => !meetsGasRequirement(diverProfile.gasCertifications, requirement)
  );
  const meetsRequirements = meetsCertification && missingGasCertifications.length === 0;
  const chips: string[] = [];

  if (requiredCertification) {
    chips.push(`Requires ${getCertReqDisplayName(requiredCertification)}`);
  }

  requiredGasCertifications.forEach((requirement) => {
    chips.push(`Requires ${getGasCertificationDisplayName(requirement)}`);
  });

  let warningMessage: string | undefined;
  if (!meetsRequirements) {
    const missingParts: string[] = [];

    if (!meetsCertification && requiredCertification) {
      missingParts.push(getCertReqDisplayName(requiredCertification));
    }

    if (missingGasCertifications.length > 0) {
      missingParts.push(...missingGasCertifications.map((requirement) => getGasCertificationDisplayName(requirement)));
    }

    warningMessage = `Your profile does not currently include ${missingParts.join(' + ')}.`;
  }

  return {
    requiredCertification,
    requiredGasCertifications,
    meetsCertificationRequirement: meetsCertification,
    missingGasCertifications,
    meetsRequirements,
    note: getEffectiveRequirementNote(site, requiredCertification),
    chips,
    warningMessage,
  };
};